// Navigation & Event Listeners

import { state } from './config.js';
import { prefersReducedMotion, isValidEmail } from '../../shared/js/utils/index.js';
import { showFormError, clearFormError } from './forms.js';
import { closeCheckoutModal, closeEnterpriseModal } from './modals.js';

function closeMobileMenu({ restoreFocus = true } = {}) {
    const mobileMenu = document.getElementById('mobileMenu');
    const menuBtn = document.querySelector('.mobile-menu-btn');
    if (!mobileMenu || !menuBtn) return;

    mobileMenu.classList.remove('active');
    menuBtn.classList.remove('active');
    menuBtn.setAttribute('aria-expanded', 'false');

    if (state.mobileMenuFocusTrapHandler) {
        document.removeEventListener('keydown', state.mobileMenuFocusTrapHandler);
        state.mobileMenuFocusTrapHandler = null;
    }

    if (restoreFocus && state.mobileMenuPreviousFocus && state.mobileMenuPreviousFocus.focus) {
        state.mobileMenuPreviousFocus.focus();
    }
    state.mobileMenuPreviousFocus = null;
}

function scrollToSection(target, hash, options = {}) {
    const navbar = document.querySelector('.navbar');
    const navHeight = navbar ? navbar.offsetHeight : 0;
    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - navHeight;
    const behavior = prefersReducedMotion() ? 'auto' : (options.behavior || 'smooth');
    window.scrollTo({ top: targetPosition, behavior });

    if (hash) {
        window.history.replaceState(null, '', hash);
    }
}

/**
 * Toggle mobile menu with focus management
 */
export function toggleMobileMenu() {
    const mobileMenu = document.getElementById('mobileMenu');
    const menuBtn = document.querySelector('.mobile-menu-btn');
    if (mobileMenu && menuBtn) {
        const isExpanded = mobileMenu.classList.toggle('active');
        menuBtn.classList.toggle('active');
        menuBtn.setAttribute('aria-expanded', isExpanded);

        if (isExpanded) {
            state.mobileMenuPreviousFocus = document.activeElement;
            const firstLink = mobileMenu.querySelector('a');
            if (firstLink) {
                firstLink.focus();
            }

            state.mobileMenuFocusTrapHandler = (e) => trapFocusInMobileMenu(e, mobileMenu, menuBtn);
            document.addEventListener('keydown', state.mobileMenuFocusTrapHandler);
        } else {
            closeMobileMenu();
        }
    }
}

function trapFocusInMobileMenu(e, mobileMenu, menuBtn) {
    if (e.key === 'Escape') {
        toggleMobileMenu();
        return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = mobileMenu.querySelectorAll('a, button');
    const allFocusable = [menuBtn, ...focusableElements];
    const firstFocusable = allFocusable[0];
    const lastFocusable = allFocusable[allFocusable.length - 1];

    if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
        }
    } else {
        if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
        }
    }
}

/**
 * Setup event listeners
 */
export function setupEventListeners() {
    // Smooth scroll for anchor links
    document.addEventListener('click', function(e) {
        const anchor = e.target.closest('a[href^="#"]');
        if (!anchor) return;

        const href = anchor.getAttribute('href');
        if (!href || href.length <= 1) return;

        const target = document.getElementById(href.substring(1));
        if (!target) return;

        e.preventDefault();
        scrollToSection(target, href);
        closeMobileMenu({ restoreFocus: false });
    });

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            closeCheckoutModal();
            closeEnterpriseModal();
            closeMobileMenu();
        }
    });

    // Close mobile menu when clicking outside the menu/nav button
    document.addEventListener('click', (e) => {
        const mobileMenu = document.getElementById('mobileMenu');
        const menuBtn = document.querySelector('.mobile-menu-btn');
        if (!mobileMenu || !menuBtn || !mobileMenu.classList.contains('active')) return;

        const clickedInsideMenu = mobileMenu.contains(e.target);
        const clickedMenuButton = menuBtn.contains(e.target);
        if (!clickedInsideMenu && !clickedMenuButton) {
            closeMobileMenu({ restoreFocus: false });
        }
    });

    // Ensure open mobile menu is closed when switching to desktop nav
    window.addEventListener('resize', () => {
        if (window.innerWidth > 768) {
            closeMobileMenu({ restoreFocus: false });
        }
    });

    // Real-time email validation for download form
    const downloadEmail = document.getElementById('downloadEmail');
    if (downloadEmail) {
        downloadEmail.addEventListener('blur', function() {
            if (this.value.trim() && !isValidEmail(this.value.trim())) {
                showFormError('downloadEmail', 'downloadEmailError', 'Please enter a valid email address');
            }
        });
        downloadEmail.addEventListener('input', function() {
            if (this.classList.contains('error')) {
                clearFormError('downloadEmail', 'downloadEmailError');
            }
        });
    }

    // Real-time email validation for checkout form
    const checkoutEmail = document.getElementById('checkoutEmail');
    if (checkoutEmail) {
        checkoutEmail.addEventListener('blur', function() {
            if (this.value.trim() && !isValidEmail(this.value.trim())) {
                showFormError('checkoutEmail', 'checkoutEmailError', 'Please enter a valid email address');
            }
        });
        checkoutEmail.addEventListener('input', function() {
            if (this.classList.contains('error')) {
                clearFormError('checkoutEmail', 'checkoutEmailError');
            }
        });
    }

    // Focus trap helper function
    function setupFocusTrap(modalElement) {
        if (!modalElement) return;
        modalElement.addEventListener('keydown', function(e) {
            if (e.key === 'Tab') {
                const focusableElements = modalElement.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey && document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                } else if (!e.shiftKey && document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        });
    }

    // Focus trap for modals
    setupFocusTrap(document.getElementById('checkoutModal'));
    setupFocusTrap(document.getElementById('enterpriseModal'));

    // Support direct deep-link navigation with sticky header offset.
    if (window.location.hash && window.location.hash.length > 1) {
        const target = document.getElementById(window.location.hash.substring(1));
        if (target) {
            requestAnimationFrame(() => scrollToSection(target, window.location.hash, { behavior: 'auto' }));
        }
    }
}
